// src/modules/callSync/constants.js

// Mirrors constants/callSyncEnums.js on the backend.
export const CALL_STATUS_META = {
  COMMAND_CREATED: { label: 'Preparing…', color: 'default', live: true },
  COMMAND_SENT: { label: 'Calling phone…', color: 'processing', live: true },
  COMMAND_DELIVERED: { label: 'Phone notified', color: 'processing', live: true },
  CALL_INITIATED: { label: 'Call initiated', color: 'processing', live: true },
  DIALING: { label: 'Dialing…', color: 'processing', live: true },
  CONNECTED: { label: 'Connected', color: 'success', live: true },
  DISCONNECTED: { label: 'Completed', color: 'success', live: false },
  NO_ANSWER: { label: 'No Answer', color: 'warning', live: false },
  FAILED: { label: 'Failed', color: 'error', live: false },
  EXPIRED: { label: 'Expired', color: 'error', live: false },
  CANCELLED: { label: 'Cancelled', color: 'default', live: false },
};

export const statusMeta = (s) => CALL_STATUS_META[s] || { label: s || '—', color: 'default', live: false };

// Statuses the phone has NOT yet acted on. Up to here "cancel" simply means
// "do not dial", and any handset can honour it.
const CANCELLABLE_STATUSES = ['COMMAND_CREATED', 'COMMAND_SENT', 'COMMAND_DELIVERED'];

/**
 * Can the CRM stop this call?
 *
 * Two different things wear the same button. Before the phone dials, cancelling
 * is just withdrawing the command. After it dials, stopping the call means
 * HANGING UP, which only a handset that is the device's default dialer can do —
 * the backend reports that per call as `canEndRemotely`. Offering the button
 * without it promises something the system cannot deliver, which is exactly why
 * the old Cancel button appeared to do nothing mid-call.
 *
 * @returns {null|'CANCEL'|'END'} which action is genuinely available
 */
export function stopAction(call) {
  if (!call || !statusMeta(call.status).live) return null;
  if (CANCELLABLE_STATUSES.includes(call.status)) return 'CANCEL';
  return call.canEndRemotely ? 'END' : null;
}

export const canCancel = (status) => CANCELLABLE_STATUSES.includes(status);

// Duration figures that are a MEASUREMENT rather than an upper bound. Mirrors
// MEASURED_DURATION_SOURCES in constants/callSyncEnums.js.
export const MEASURED_SOURCES = ['TELECOM', 'CALL_LOG'];

export const isMeasured = (call) => MEASURED_SOURCES.includes(call?.durationSource);

// Reuses the existing lead CALL_OUTCOME vocabulary, so a disposition flows
// straight into LeadActivity and the existing lead pipeline.
export const DISPOSITION_OPTIONS = [
  { value: 'INTERESTED', label: 'Interested' },
  { value: 'CALL_BACK_LATER', label: 'Follow-up Required', requiresFollowUp: true },
  { value: 'NOT_INTERESTED', label: 'Not Interested' },
  { value: 'NO_ANSWER', label: 'No Answer' },
  { value: 'BUSY', label: 'Busy' },
  { value: 'SWITCHED_OFF', label: 'Switched Off' },
  { value: 'WRONG_NUMBER', label: 'Wrong Number' },
  { value: 'VISIT_BOOKED', label: 'Visit Booked' },
  // Disabled for now: converting a lead isn't available yet.
  { value: 'CONVERTED', label: 'Converted', disabled: true },
  { value: 'DND', label: 'Do Not Disturb' },
  { value: 'OTHER', label: 'Other' },
];

export const dispositionLabel = (v) => DISPOSITION_OPTIONS.find((o) => o.value === v)?.label || v || '—';

// ── Daily-target credit ──
// Mirrors TARGET_CREDIT_MODE in constants/callSyncEnums.js.
export const CREDIT_MODE_OPTIONS = [
  {
    value: 'DIALLED_AND_SAVED',
    label: 'Dialled AND result saved',
    short: 'Result saved',
    help: 'A call counts once its outcome is recorded in the CRM — connected or not. A dialled call with no saved result earns nothing.',
  },
  {
    value: 'CONNECTED_AND_SAVED',
    label: 'Connected AND result saved',
    short: 'Connected + saved',
    help: 'Strictest: only calls the customer actually answered, whose outcome was then recorded, count.',
  },
  {
    value: 'DIALLED_ONLY',
    label: 'Dialled only',
    short: 'Dialled only',
    help: 'The original behaviour: a call counts the moment the phone places it, whether or not the result is ever saved.',
  },
];

export const creditModeMeta = (v) => CREDIT_MODE_OPTIONS.find((o) => o.value === v)
  || { value: v, label: v || '—', short: v || '—', help: '' };

// Why a call did or did not earn credit. Mirrors CREDIT_REASON on the backend.
export const CREDIT_REASON_LABEL = {
  CREDITED: 'Counted',
  NOT_DIALLED: 'Never dialled',
  NOT_CONNECTED: 'Not answered',
  RESULT_NOT_SAVED: 'Awaiting result',
  SAVED_TOO_LATE: 'Saved too late',
  NOT_TRACKED: 'Not CRM-tracked',
};

// Errors the backend returns with a `code`. Each maps to a message that tells
// the user what to actually DO — never a silent failure (STEP 19).
export const CALL_ERROR_HINTS = {
  NO_DEVICE: 'No Android device is connected to this sales representative. Install the Call Sync app and sign in on the phone.',
  DEVICE_OFFLINE: 'The phone has not checked in recently. Ask the rep to open the Call Sync app.',
  DEVICE_NOT_READY: 'The phone is registered but notifications are not enabled. Open the app and allow notifications.',
  PERMISSION_MISSING: 'The phone is missing the Call permission. On the phone: app → Settings → grant Phone access.',
  NO_SIM: 'No usable SIM or cellular service on the device.',
  INVALID_PHONE_NUMBER: 'This lead has no valid mobile number. Correct the number on the lead first.',
  CALL_IN_PROGRESS: 'A call is already running on this device. Finish it before starting another.',
  PUSH_FAILED: 'Could not reach the phone. Check that it has internet and try again.',
  RATE_LIMITED: 'You are calling too quickly. Wait a moment and try again.',
  FORBIDDEN: 'You do not have permission to call this lead.',
};

/** Seconds → "5m 26s" / "8s". */
export function formatDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return `${m}m ${rem}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/** Seconds → "00:42", for the live ticking timer. */
export function formatClock(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/** Seconds → "3h 21m", for talk-time totals. */
export function formatTalkTime(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s % 60}s`;
  return `${s}s`;
}
